import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { ProjectBasedLearningResponseDto } from "@packages/api/madlenAPI.schemas";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface ProjectBasedLearningCardProps {
  project: ProjectBasedLearningResponseDto;
  customizationSettings: PdfCustomizationSettings;
}

const styles = StyleSheet.create({
  projectContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#ffffff",
  },
  section: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 6,
  },
  sectionIcon: {
    width: 16,
    height: 16,
    marginRight: 8,
    backgroundColor: "#3b82f6",
    borderRadius: 8,
  },
  // Description section styles
  descriptionHeader: {
    backgroundColor: "#dbeafe",
  },
  descriptionTitle: {
    color: "#2563eb",
    fontSize: 12,
    fontWeight: "bold",
  },
  // Advanced fields section styles
  advancedSectionContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e2e8f0",
  },
  advancedGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  advancedItem: {
    flex: 1,
    minWidth: "45%",
    padding: 8,
    backgroundColor: "#ffffff",
    borderRadius: 6,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  advancedItemTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 4,
    color: "#374151",
  },
  // Problem Type section styles
  problemHeader: {
    backgroundColor: "#fee2e2",
  },
  problemTitle: {
    color: "#dc2626",
    fontSize: 12,
    fontWeight: "bold",
  },
  // Real Life Connection section styles
  connectionHeader: {
    backgroundColor: "#dcfce7",
  },
  connectionTitle: {
    color: "#16a34a",
    fontSize: 12,
    fontWeight: "bold",
  },
  // Process Steps section styles
  stepsHeader: {
    backgroundColor: "#f3e8ff",
  },
  stepsTitle: {
    color: "#9333ea",
    fontSize: 12,
    fontWeight: "bold",
  },
  processStep: {
    flexDirection: "row",
    marginBottom: 8,
    alignItems: "flex-start",
  },
  stepNumber: {
    width: 20,
    height: 20,
    backgroundColor: "#9333ea",
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
    marginTop: 2,
  },
  stepNumberText: {
    color: "#ffffff",
    fontSize: 8,
    fontWeight: "bold",
  },
  stepContent: {
    flex: 1,
    paddingLeft: 4,
  },
  // Feedback Points section styles
  feedbackHeader: {
    backgroundColor: "#fef3c7",
  },
  feedbackTitle: {
    color: "#d97706",
    fontSize: 12,
    fontWeight: "bold",
  },
  // Assessment section styles
  assessmentHeader: {
    backgroundColor: "#fee2e2",
  },
  assessmentTitle: {
    color: "#dc2626",
    fontSize: 12,
    fontWeight: "bold",
  },
  // Peer/Self Assessment section styles
  peerSelfHeader: {
    backgroundColor: "#ede9fe",
  },
  peerSelfTitle: {
    color: "#7c3aed",
    fontSize: 12,
    fontWeight: "bold",
  },
  listItem: {
    marginBottom: 6,
    paddingLeft: 12,
    position: "relative",
  },
  listBullet: {
    position: "absolute",
    left: 0,
    top: 4,
    width: 4,
    height: 4,
    backgroundColor: "#6b7280",
    borderRadius: 2,
  },
  subSection: {
    marginBottom: 12,
    paddingLeft: 8,
  },
  subSectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 6,
    color: "#4b5563",
  },
  criterionItem: {
    marginBottom: 8,
    padding: 6,
    backgroundColor: "#f9fafb",
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: "#dc2626",
  },
  criterionTitle: {
    fontSize: 9,
    fontWeight: "bold",
    marginBottom: 4,
    color: "#374151",
  },
  levelItem: {
    marginBottom: 3,
    paddingLeft: 8,
    position: "relative",
  },
  levelBullet: {
    position: "absolute",
    left: 0,
    top: 3,
    width: 3,
    height: 3,
    backgroundColor: "#9ca3af",
    borderRadius: 1.5,
  },
  questionItem: {
    marginBottom: 6,
    padding: 6,
    backgroundColor: "#f3f4f6",
    borderRadius: 4,
    flexDirection: "row",
    alignItems: "flex-start",
  },
  questionBullet: {
    width: 4,
    height: 4,
    backgroundColor: "#7c3aed",
    borderRadius: 2,
    marginRight: 8,
    marginTop: 4,
  },
  questionText: {
    flex: 1,
  },
});

const ProjectBasedLearningDocument = ({
  project,
  customizationSettings,
}: ProjectBasedLearningCardProps) => {
  const { t } = useTranslation();
  const markdownStyle = {
    text: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
    listItem: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };

  return (
    <PdfLayout
      courseName={project.courseName || ""}
      gradeLevel={project.gradeLevel || 0}
      topic={project.title || project.prompt || ""}
      imageUrl={project.imageUrl}
      headerTextColor="#dc2626"
      customizationSettings={customizationSettings}
      author={project.authorName || ""}
      createdDate={project.createdAt || ""}
    >
      <View style={styles.projectContainer}>
        {/* Description Section */}
        {project.description && (
          <View wrap={false} style={styles.section}>
            <View style={[styles.sectionHeader, styles.descriptionHeader]}>
              <Text
                style={[
                  styles.descriptionTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.descriptionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.description")}
              </Text>
            </View>
            <View style={{ paddingLeft: 12 }}>
              <MarkdownRenderer
                content={project.description}
                customStyle={markdownStyle}
              />
            </View>
          </View>
        )}

        {/* Project Problem Section */}
        {project.projectProblem?.questions?.length && (
          <View style={styles.section} wrap={false}>
            <View style={[styles.sectionHeader, styles.problemHeader]}>
              <Text
                style={[
                  styles.problemTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.problemTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.projectProblem")}
              </Text>
            </View>
            {project.projectProblem.questions.map((question, index) => (
              <View key={index} style={styles.listItem}>
                <View style={styles.listBullet} />
                <MarkdownRenderer
                  content={question}
                  customStyle={markdownStyle}
                />
              </View>
            ))}
          </View>
        )}

        {/* Real Life Connection Section */}
        {project.realLifeConnection && (
          <View style={styles.section} wrap={false}>
            <View style={[styles.sectionHeader, styles.connectionHeader]}>
              <Text
                style={[
                  styles.connectionTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.connectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.realLifeConnection")}
              </Text>
            </View>
            <View style={{ paddingLeft: 12 }}>
              <MarkdownRenderer
                content={project.realLifeConnection}
                customStyle={markdownStyle}
              />
            </View>
          </View>
        )}

        {/* Process Steps Section */}
        {project.processSteps?.length && (
          <View style={styles.section} wrap={false}>
            <View style={[styles.sectionHeader, styles.stepsHeader]}>
              <Text
                style={[
                  styles.stepsTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.stepsTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.processSteps")}
              </Text>
            </View>
            {project.processSteps.map((step, index) => (
              <View key={index} style={styles.processStep}>
                <View style={styles.stepNumber}>
                  <Text
                    style={[
                      styles.stepNumberText,
                      {
                        fontSize: scaleFontSize(
                          styles.stepNumberText.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                    ]}
                  >
                    {index + 1}
                  </Text>
                </View>
                <View style={styles.stepContent}>
                  <MarkdownRenderer
                    content={step}
                    customStyle={markdownStyle}
                  />
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Feedback Points Section */}
        {project.feedbackPoints?.length && (
          <View style={styles.section} wrap={false}>
            <View style={[styles.sectionHeader, styles.feedbackHeader]}>
              <Text
                style={[
                  styles.feedbackTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.feedbackTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.feedbackPoints")}
              </Text>
            </View>
            {project.feedbackPoints.map((point, index) => (
              <View key={index} style={styles.listItem}>
                <View style={styles.listBullet} />
                <MarkdownRenderer content={point} customStyle={markdownStyle} />
              </View>
            ))}
          </View>
        )}

        {/* Assessment Section */}
        {project.assessment && (
          <View style={styles.section} wrap={false}>
            <View style={[styles.sectionHeader, styles.assessmentHeader]}>
              <Text
                style={[
                  styles.assessmentTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.assessmentTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.assessment")}
              </Text>
            </View>

            {/* Assessment Criteria */}
            {project.assessment.criteria?.length && (
              <View style={styles.subSection}>
                <Text
                  style={[
                    styles.subSectionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.subSectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("projectBasedLearning.assessmentCriteria")}:
                </Text>
                {project.assessment.criteria.map((criterion, index) => (
                  <View key={index} style={styles.listItem}>
                    <View style={styles.listBullet} />
                    <MarkdownRenderer
                      content={criterion}
                      customStyle={markdownStyle}
                    />
                  </View>
                ))}
              </View>
            )}

            {/* Assessment Rubric */}
            {project.assessment.rubric?.some(
              (item) => item?.criterion && item?.levels,
            ) && (
              <View style={styles.subSection}>
                <Text
                  style={[
                    styles.subSectionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.subSectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("projectBasedLearning.assessmentRubric")}:
                </Text>
                {project.assessment.rubric
                  .filter((item) => item?.criterion && item?.levels)
                  .map((item: any, index: number) => (
                    <View key={index} style={styles.criterionItem}>
                      <Text
                        style={[
                          styles.criterionTitle,
                          {
                            fontSize: scaleFontSize(
                              styles.criterionTitle.fontSize,
                              customizationSettings?.fontSize,
                            ),
                          },
                        ]}
                      >
                        {item.criterion}
                      </Text>
                      {item.levels?.map((level: string, levelIndex: number) => (
                        <View key={levelIndex} style={styles.levelItem}>
                          <View style={styles.levelBullet} />
                          <MarkdownRenderer
                            content={level}
                            customStyle={markdownStyle}
                          />
                        </View>
                      ))}
                    </View>
                  ))}
              </View>
            )}
          </View>
        )}

        {/* Peer/Self Assessment Section */}
        {project.peerSelfAssessment && (
          <View style={styles.section} wrap={false}>
            <View style={[styles.sectionHeader, styles.peerSelfHeader]}>
              <Text
                style={[
                  styles.peerSelfTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.peerSelfTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.peerSelfAssessment")}
              </Text>
            </View>

            {/* Peer Review Questions */}
            {project.peerSelfAssessment.peer_review_questions?.length && (
              <View style={styles.subSection}>
                <Text
                  style={[
                    styles.subSectionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.subSectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("projectBasedLearning.peerReviewQuestions")}:
                </Text>
                {project.peerSelfAssessment.peer_review_questions.map(
                  (question, index) => (
                    <View key={index} style={styles.questionItem}>
                      <View style={styles.questionBullet} />
                      <View style={styles.questionText}>
                        <MarkdownRenderer
                          content={question}
                          customStyle={markdownStyle}
                        />
                      </View>
                    </View>
                  ),
                )}
              </View>
            )}

            {/* Self Reflection Questions */}
            {project.peerSelfAssessment.self_reflection_questions?.length && (
              <View style={styles.subSection}>
                <Text
                  style={[
                    styles.subSectionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.subSectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("projectBasedLearning.selfReflectionQuestions")}:
                </Text>
                {project.peerSelfAssessment.self_reflection_questions.map(
                  (question, index) => (
                    <View key={index} style={styles.questionItem}>
                      <View style={styles.questionBullet} />
                      <View style={styles.questionText}>
                        <MarkdownRenderer
                          content={question}
                          customStyle={markdownStyle}
                        />
                      </View>
                    </View>
                  ),
                )}
              </View>
            )}
          </View>
        )}
      </View>
    </PdfLayout>
  );
};

function ProjectBasedLearningCardPDF(props: ProjectBasedLearningCardProps) {
  return <ProjectBasedLearningDocument {...props} />;
}

export default ProjectBasedLearningCardPDF;
